/**
 * TableView - Renders items in a sortable table with optional bulk editing.
 *
 * Props:
 *   items         - Item[] — the filtered+sorted items to display
 *   columns       - TableColumnConfig[] — from the collectionAdapter's renderTableColumns
 *   sortField     - string — currently active sort field key
 *   sortDirection - 'asc' | 'desc' — current sort direction
 *   onSort        - (field: string) => void — called when a sortable column header is clicked
 *   bulkEditFields - BulkEditFieldConfig[] — optional, enables checkboxes + bulk edit toolbar
 *   onBulkSave    - (items: Item[], updates: Object) => Promise — called to save bulk edits
 *   getItemId     - (item) => string — returns unique ID for an item (default: item.id)
 *
 * TableColumnConfig shape:
 *   { key, header, sortable?, renderCell, width? }
 *
 * BulkEditFieldConfig shape:
 *   { key, label, type: 'select' | 'rating', options? }
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useUsers } from '../hooks/useUsers.jsx';
import useUserView from '../hooks/useUserView.jsx';
import { usePod } from '../hooks/usePod.jsx';
import { StarRating, useLongPress } from '@code-burrow/design-system';

function TableRow({ item, index, isSelected, hasBulkEdit, toggleItem, columns, onItemUpdated, onRowLongPress }) {
    const longPressHandlers = useLongPress(
        ({ x, y }) => onRowLongPress?.(item, { x, y }),
    );
    const rowProps = onRowLongPress ? longPressHandlers : {};
    return (
        <tr
            className={[
                index % 2 === 0 ? 'bg-bg-primary' : 'bg-surface',
                isSelected ? 'bg-accent-primary/5' : '',
                'hover:bg-surface-hover transition-colors duration-150',
            ].join(' ')}
            {...rowProps}
        >
            {hasBulkEdit && (
                <td className="px-3 py-4">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleItem(item)}
                        className="h-4 w-4 rounded-sm border border-border text-accent-primary focus:ring-accent-primary cursor-pointer"
                        aria-label={`Select ${item.title || 'item'}`}
                    />
                </td>
            )}
            {columns.map((col) => (
                <td
                    key={col.key}
                    className="px-4 py-4 font-body text-sm text-text-secondary"
                    style={col.width ? { minWidth: col.width, width: col.width } : undefined}
                >
                    {col.renderCell(item, { onItemUpdated })}
                </td>
            ))}
        </tr>
    );
}

export default function TableView({ items, columns, sortField, sortDirection, onSort, bulkEditFields, onBulkSave, onBulkDelete, getItemId, onItemUpdated, onRowLongPress }) {
    const hasBulkEdit = bulkEditFields?.length > 0 && onBulkSave;

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkSaving, setBulkSaving] = useState(false);

    const getId = getItemId || ((item) => item.id);

    const allSelected = items.length > 0 && items.every((item) => selectedIds.has(getId(item)));
    const someSelected = selectedIds.size > 0;

    const toggleAll = useCallback(() => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(items.map(getId)));
        }
    }, [allSelected, items, getId]);

    const toggleItem = useCallback((item) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const id = getId(item);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, [getId]);

    const handleBulkSave = useCallback(async (updates) => {
        setBulkSaving(true);
        try {
            const selectedItems = items.filter((item) => selectedIds.has(getId(item)));
            await onBulkSave(selectedItems, updates);
            setSelectedIds(new Set());
        } finally {
            setBulkSaving(false);
        }
    }, [items, selectedIds, getId, onBulkSave]);

    const handleBulkDelete = useCallback(async () => {
        if (!onBulkDelete) return;
        setBulkSaving(true);
        try {
            const selectedItems = items.filter((item) => selectedIds.has(getId(item)));
            await onBulkDelete(selectedItems);
            setSelectedIds(new Set());
        } finally {
            setBulkSaving(false);
        }
    }, [items, selectedIds, getId, onBulkDelete]);

    return (
        <div>
            {/* Bulk edit toolbar */}
            {hasBulkEdit && someSelected && (
                <BulkEditToolbar
                    selectedCount={selectedIds.size}
                    fields={bulkEditFields}
                    onSave={handleBulkSave}
                    onDelete={onBulkDelete ? handleBulkDelete : undefined}
                    onClear={() => setSelectedIds(new Set())}
                    saving={bulkSaving}
                />
            )}

            {/* Table */}
            <div className="bg-surface rounded-lg border border-border-subtle overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div style={{ minWidth: Math.max(600, columns.length * 120) }}>
                    <table className="w-full divide-y divide-border-subtle">
                        <thead className="bg-surface-hover sticky top-0 z-10">
                            <tr>
                                {hasBulkEdit && (
                                    <th scope="col" className="px-3 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            className="h-4 w-4 rounded-sm border border-border text-accent-primary focus:ring-accent-primary cursor-pointer"
                                            aria-label="Select all"
                                        />
                                    </th>
                                )}
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        className={[
                                            'px-4 py-3 text-left font-body text-sm font-semibold text-text-primary',
                                            col.sortable
                                                ? 'cursor-pointer hover:bg-surface-active transition-colors duration-150'
                                                : '',
                                        ].join(' ')}
                                        style={col.width ? { minWidth: col.width, width: col.width } : undefined}
                                        onClick={col.sortable ? () => onSort(col.key) : undefined}
                                    >
                                        <div className="flex items-center min-h-[44px]">
                                            {col.header}
                                            {col.sortable && (
                                                <SortIndicator
                                                    active={sortField === col.key}
                                                    direction={sortDirection}
                                                />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-subtle">
                            {items.map((item, index) => {
                                const id = getId(item);
                                const isSelected = selectedIds.has(id);
                                return (
                                    <TableRow
                                        key={id ?? index}
                                        item={item}
                                        index={index}
                                        isSelected={isSelected}
                                        hasBulkEdit={hasBulkEdit}
                                        toggleItem={toggleItem}
                                        columns={columns}
                                        onItemUpdated={onItemUpdated}
                                        onRowLongPress={onRowLongPress}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function PodMembersSelect({ users, activeView, value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const others = users.filter(u => u.name !== activeView);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    if (others.length === 0) return null;

    const selected = value || [];
    const allSelected = others.every(u => selected.includes(u.id));
    const label = selected.length === 0
        ? '—'
        : allSelected
            ? `All (${others.map(u => u.emoji).join('')})`
            : others.filter(u => selected.includes(u.id)).map(u => u.emoji).join(' ');

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className={`h-7 px-2 pr-6 rounded-md border text-xs font-medium transition-colors cursor-pointer truncate max-w-[140px] ${
                    selected.length > 0
                        ? 'bg-accent-primary/15 border-accent-primary text-accent-primary'
                        : 'bg-bg-secondary border-border text-text-secondary hover:text-text-primary'
                }`}
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.4rem center',
                }}
            >
                {label}
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1">
                    {/* Select All */}
                    <button
                        type="button"
                        onClick={() => onChange(allSelected ? [] : others.map(u => u.id))}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-surface-hover ${
                            allSelected ? 'text-accent-primary font-medium' : 'text-text-secondary'
                        }`}
                    >
                        <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center text-[9px] ${
                            allSelected ? 'bg-accent-primary border-accent-primary text-white' : 'border-border-subtle'
                        }`}>{allSelected && '✓'}</span>
                        Everyone
                    </button>
                    <div className="h-px bg-border-subtle mx-2 my-0.5" />
                    {others.map(u => {
                        const checked = selected.includes(u.id);
                        return (
                            <button
                                key={u.id}
                                type="button"
                                onClick={() => {
                                    onChange(checked
                                        ? selected.filter(id => id !== u.id)
                                        : [...selected, u.id]
                                    );
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-surface-hover ${
                                    checked ? 'text-text-primary' : 'text-text-tertiary'
                                }`}
                            >
                                <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center text-[9px] ${
                                    checked ? 'bg-accent-primary border-accent-primary text-white' : 'border-border-subtle'
                                }`}>{checked && '✓'}</span>
                                {u.emoji} {u.displayName || u.name}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function BulkEditToolbar({ selectedCount, fields, onSave, onClear, saving, onDelete }) {
    const [values, setValues] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { activeView } = useUserView();
    const { users } = useUsers();
    const { members } = usePod();
    // Only show pod members in the "Together with" dropdown, not all users
    const memberUserIds = new Set((members || []).map(m => m.userId));
    const podUsers = users.filter(u => memberUserIds.has(u.id));

    const hasChanges = Object.values(values).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && v !== '' && v !== null;
    });

    const handleApply = async () => {
        if (!hasChanges) return;
        const updates = {};
        for (const [key, val] of Object.entries(values)) {
            const field = fields.find((f) => f.key === key);
            if (field?.type === 'podMembers') {
                if (Array.isArray(val) && val.length > 0) updates[key] = val;
            } else if (val !== undefined && val !== '' && val !== null) {
                if (field?.type === 'rating') {
                    updates[key] = typeof val === 'number' ? val : parseInt(val, 10);
                } else if (field?.type === 'boolean') {
                    updates[key] = typeof val === 'boolean' ? val : val === 'true';
                } else {
                    updates[key] = val;
                }
            }
        }
        await onSave(updates);
        setValues({});
    };

    return (
        <div className="mb-3 bg-accent-primary/10 border border-accent-primary/20 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-text-primary whitespace-nowrap">
                    {selectedCount} selected
                </span>
                <div className="flex items-center gap-4 flex-wrap flex-1">
                    {fields.map((f) => (
                        <div key={f.key} className="flex items-center gap-1.5">
                            <label className="text-xs text-text-tertiary whitespace-nowrap">{f.label}:</label>
                            {f.type === 'select' && (
                                <select
                                    value={values[f.key] || ''}
                                    onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                    className="rounded-md border border-border bg-bg-secondary text-text-primary px-2 py-1 text-xs [color-scheme:dark] focus:outline-none focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
                                >
                                    <option value="">—</option>
                                    {(f.options || []).map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}
                            {f.type === 'rating' && (
                                <StarRating
                                    rating={typeof values[f.key] === 'number' ? values[f.key] : null}
                                    size="h-4 w-4"
                                    onChange={(val) => setValues((prev) => ({ ...prev, [f.key]: val }))}
                                />
                            )}
                            {f.type === 'podMembers' && (
                                <PodMembersSelect
                                    users={podUsers}
                                    activeView={activeView}
                                    value={values[f.key] || []}
                                    onChange={(names) => setValues((prev) => ({ ...prev, [f.key]: names }))}
                                />
                            )}
                            {f.type === 'boolean' && (
                                <button
                                    role="switch"
                                    aria-checked={values[f.key] === true || values[f.key] === 'true'}
                                    onClick={() => {
                                        setValues((prev) => {
                                            const current = prev[f.key];
                                            // Cycle: undefined → true → false → undefined
                                            if (current === undefined || current === '' || current === null) return { ...prev, [f.key]: true };
                                            if (current === true || current === 'true') return { ...prev, [f.key]: false };
                                            return { ...prev, [f.key]: undefined };
                                        });
                                    }}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                                        values[f.key] === true || values[f.key] === 'true'
                                            ? 'bg-accent-primary/60'
                                            : values[f.key] === false || values[f.key] === 'false'
                                                ? 'bg-error/40'
                                                : 'bg-text-tertiary/30'
                                    }`}
                                >
                                    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                                        values[f.key] === true || values[f.key] === 'true' ? 'translate-x-4' : 'translate-x-0'
                                    }`} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleApply}
                        disabled={!hasChanges || saving}
                        className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent-primary text-text-inverse hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Applying…' : 'Apply'}
                    </button>
                    {onDelete && (
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={saving || hasChanges}
                            className="px-3 py-1.5 rounded-md text-xs font-medium bg-error/80 text-white hover:bg-error transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Delete
                        </button>
                    )}
                    <button
                        onClick={onClear}
                        disabled={saving}
                        className="px-3 py-1.5 rounded-md text-xs font-medium border border-border text-text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {/* Delete confirmation overlay */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="bg-surface rounded-lg p-6 mx-4 max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display text-lg font-semibold text-text-primary mb-2">Delete {selectedCount} item{selectedCount !== 1 ? 's' : ''}?</h3>
                        <p className="text-sm text-text-secondary mb-4">
                            {selectedCount === 1 ? 'This item' : 'These items'} will be hidden from your collection. They won't reappear on future imports.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowDeleteConfirm(false); onDelete(); }}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-error text-text-inverse hover:brightness-90 transition-all"
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 rounded-md text-sm font-medium bg-surface-hover text-text-secondary hover:bg-surface-active transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SortIndicator({ active, direction }) {
    if (!active) {
        return (
            <svg className="ml-1 h-4 w-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
        );
    }
    return direction === 'asc' ? (
        <svg className="ml-1 h-4 w-4 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
    ) : (
        <svg className="ml-1 h-4 w-4 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    );
}
